"use client"

import type { FunctionComponent, MouseEvent } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"
import { useSearchParams } from "next/navigation"
import { useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { SortDescending } from "@jobteaser/spark/components/Icons/SortDescending"

import { DEFAULT_RESULTS_SORT_OPTION } from "@/features/companiesSearch/constants"
import { useLayoutContext } from "@/modules/landingSearch/components/Layout/LayoutContextProvider"

import styles from "./ResultsSort.module.css"

type ResultsSortProps = {
  defaultLabel: string
  label: string
  nameAscLabel: string
  nameDescLabel: string
}

type SortOption = {
  label: string
  value: "default" | "name-asc" | "name-desc"
}

const ResultsSortOptionDynamic = dynamic(() => import("./ResultSortOptions").then(mod => mod.ResultsSortOptions))

export const ResultsSort: FunctionComponent<ResultsSortProps> = ({
  defaultLabel,
  label,
  nameAscLabel,
  nameDescLabel,
}) => {
  const searchParams = useSearchParams()
  const { isSearchLoading } = useLayoutContext()

  const sortOptions: [SortOption, SortOption, ...SortOption[]] = [
    {
      label: defaultLabel,
      value: "default",
    },
    {
      label: nameAscLabel,
      value: "name-asc",
    },
    {
      label: nameDescLabel,
      value: "name-desc",
    },
  ]

  const { label: valueLabel, value } =
    sortOptions.find(option => option.value === searchParams.get("sort")) ||
    sortOptions.find(option => option.value === DEFAULT_RESULTS_SORT_OPTION) ||
    sortOptions[0]

  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation()
    setIsExpanded(!isExpanded)
  }

  const handleClose = (): void => {
    setIsExpanded(false)
  }

  return (
    <div className={styles.main}>
      <Button
        aria-label={valueLabel}
        aria-readonly={isSearchLoading}
        className={clsx(styles.button, {
          [styles.button___expanded]: isExpanded,
          [styles.button___readonly]: isSearchLoading,
        })}
        icon={SortDescending}
        onClick={handleClick}
        variant="tertiary"
      />
      {isExpanded && (
        <ResultsSortOptionDynamic label={label} onClose={handleClose} options={sortOptions} value={value} />
      )}
    </div>
  )
}
